import {
  MCP_READ_SCOPE,
  MCP_RESULTS_READ_SCOPE,
  MCP_WRITE_SCOPE,
  McpScope,
} from "./McpScopes";

export type McpToolInfo = {
  /** Tool name as advertised over MCP */
  name: string;
  /** Consent-screen label */
  title: string;
  /** One line of plain English for the consent screen */
  summary: string;
  scope: McpScope;
};

/**
 * The MCP tools a client can call, and the scope each one needs. Consent
 * screens list tools rather than raw scopes, so people can see what an AI
 * client will actually be able to do.
 *
 * mcp-server owns the tools; this is the user-facing mirror.
 * modules/toolCatalog.spec.ts fails if the two drift apart.
 */
export const MCP_TOOL_CATALOG: McpToolInfo[] = [
  {
    name: "list_pollys",
    title: "Polly list",
    summary: "Find your pollys",
    scope: MCP_READ_SCOPE,
  },
  {
    name: "get_polly",
    title: "Polly details",
    summary: "Read a polly's questions, choices, and settings",
    scope: MCP_READ_SCOPE,
  },
  {
    name: "get_draft",
    title: "Draft status",
    summary: "Check a draft that is waiting to be sent",
    scope: MCP_READ_SCOPE,
  },
  {
    name: "search_templates",
    title: "Template search",
    summary: "Browse your organization's polly templates",
    scope: MCP_READ_SCOPE,
  },
  {
    name: "draft_polly",
    title: "Draft a polly",
    summary: "Prepare a polly for you to review before it sends",
    scope: MCP_WRITE_SCOPE,
  },
  {
    name: "create_polly",
    title: "Send a polly",
    summary: "Post a polly to the channels and people you chose",
    scope: MCP_WRITE_SCOPE,
  },
  {
    name: "update_polly",
    title: "Update polly",
    summary: "Change a polly's questions, choices, or schedule",
    scope: MCP_WRITE_SCOPE,
  },
  {
    name: "close_polly",
    title: "Close polly",
    summary: "Stop collecting responses",
    scope: MCP_WRITE_SCOPE,
  },
  {
    name: "reopen_polly",
    title: "Reopen polly",
    summary: "Start collecting responses again",
    scope: MCP_WRITE_SCOPE,
  },
  {
    name: "send_reminder",
    title: "Send reminders",
    summary: "Nudge people who haven't responded yet",
    scope: MCP_WRITE_SCOPE,
  },
  {
    name: "cancel_draft",
    title: "Cancel draft",
    summary: "Discard a draft before it sends",
    scope: MCP_WRITE_SCOPE,
  },
  {
    name: "get_polly_results",
    title: "Polly results",
    summary: "Read how people voted, including written answers",
    scope: MCP_RESULTS_READ_SCOPE,
  },
];

export type McpScopeCopy = {
  /** Group heading on the consent screen */
  title: string;
  /** What granting the group means, in one line */
  summary: string;
};

export const MCP_SCOPE_COPY: Record<McpScope, McpScopeCopy> = {
  [MCP_READ_SCOPE]: {
    title: "View your pollys",
    summary: "Questions, choices, and settings — not responses",
  },
  [MCP_WRITE_SCOPE]: {
    title: "Create and manage pollys",
    summary: "Send, edit, remind, close, and reopen on your behalf",
  },
  [MCP_RESULTS_READ_SCOPE]: {
    title: "Read responses",
    summary: "View results and written answers",
  },
};

export function mcpToolsForScopes(scopes: string[]): McpToolInfo[] {
  const granted = new Set(scopes);
  return MCP_TOOL_CATALOG.filter((tool) => granted.has(tool.scope));
}
